<template>
  <HeaderComponent />
  <HeroComponent 
    image="https://images.pexels.com/photos/3184460/pexels-photo-3184460.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2" 
    text="Our Services" 
    subtitle="Transforming ideas into digital excellence with our comprehensive solutions"
  />
  
  <!-- Services Overview Section -->
  <div class="py-20 bg-gray-50">
    <div class="container mx-auto px-4">
      <div class="text-center mb-16" data-sal="slide-up" data-sal-delay="200">
        <h6 class="text-sm font-medium uppercase tracking-wider text-secondary mb-4">WHAT WE DO</h6>
        <h2 class="text-4xl md:text-5xl font-bold font-heading text-gray-900 mb-6">
          Comprehensive Digital Solutions
        </h2>
        <p class="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
          From concept to launch, we provide end-to-end digital services that drive results and exceed expectations.
        </p>
      </div>

      <!-- Loading State -->
      <div v-if="isLoading" class="py-16">
        <LoadingRipple message="Loading our services..." />
      </div>

      <!-- Empty State -->
      <div v-else-if="services.length === 0" class="text-center py-16">
        <p class="text-gray-600">No services available at the moment.</p>
      </div>

      <!-- Services Grid -->
      <div v-else class="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
        <div 
          v-for="(service, index) in services" 
          :key="index"
          class="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden group hover:-translate-y-2"
          :data-sal="'slide-up'"
          :data-sal-delay="service.delay"
        >
          <div class="p-8">
            <!-- Icon -->
            <div class="w-16 h-16 bg-gradient-to-br from-primary to-secondary rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 mx-auto">
              <i :class="`${service.icon} text-white text-2xl`"></i>
            </div>
            
            <!-- Content -->
            <h3 class="text-2xl font-bold font-heading text-gray-900 mb-4 group-hover:text-primary transition-colors duration-300 text-center">
              {{ service.name }}
            </h3>
            <p class="text-gray-600 leading-relaxed mb-6">
              {{ service.description }}
            </p>
            
            <!-- Features -->
            <ul class="space-y-2 mb-6">
              <li v-for="feature in service.features" :key="feature" class="flex items-center text-sm text-gray-600">
                <svg class="w-4 h-4 text-secondary mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                </svg>
                {{ feature }}
              </li>
            </ul>
            
            <!-- CTA -->
            <router-link 
              to="/contact"
              class="inline-flex items-center text-primary hover:text-secondary font-medium transition-colors duration-300"
            >
              Get Started
              <svg class="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
              </svg>
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Process Section -->
  <div class="py-20">
    <div class="container mx-auto px-4">
      <div class="text-center mb-16" data-sal="slide-up" data-sal-delay="200">
        <h6 class="text-sm font-medium uppercase tracking-wider text-secondary mb-4">OUR PROCESS</h6>
        <h2 class="text-4xl md:text-5xl font-bold font-heading text-gray-900 mb-6">
          How We Work
        </h2>
        <p class="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
          A proven methodology that ensures quality results and seamless collaboration from start to finish.
        </p>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-4 gap-8">
        <div 
          v-for="(step, index) in processSteps" 
          :key="index"
          class="text-center"
          :data-sal="'slide-up'"
          :data-sal-delay="200 + (index * 100)"
        >
          <!-- Step Number -->
          <div class="w-16 h-16 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center text-white font-bold text-xl mx-auto mb-6 relative">
            {{ index + 1 }}
            <div v-if="index < processSteps.length - 1" class="absolute left-full top-1/2 w-full h-0.5 bg-gray-200 transform -translate-y-1/2 hidden md:block"></div>
          </div>
          
          <!-- Content -->
          <h3 class="text-xl font-bold font-heading text-gray-900 mb-3">{{ step.title }}</h3>
          <p class="text-gray-600 leading-relaxed">{{ step.description }}</p>
        </div>
      </div>
    </div>
  </div>

  <!-- CTA Section -->
  <div class="py-20 bg-gradient-to-br from-primary to-secondary">
    <div class="container mx-auto px-4 text-center">
      <div data-sal="slide-up" data-sal-delay="200">
        <h2 class="text-4xl md:text-5xl font-bold font-heading text-white mb-6">
          Ready to Start Your Project?
        </h2>
        <p class="text-xl text-white/90 mb-8 max-w-2xl mx-auto leading-relaxed">
          Let's discuss your vision and create something amazing together. Our team is ready to bring your ideas to life.
        </p>
        <div class="space-y-4 sm:space-y-0 sm:space-x-4 sm:flex sm:justify-center">
          <router-link 
            to="/contact"
            class="inline-block bg-white text-primary hover:bg-gray-100 px-8 py-4 rounded-lg font-medium transition-all duration-300 hover:shadow-lg hover:scale-105"
          >
            Get Free Consultation
          </router-link>
          <router-link 
            to="/projects"
            class="inline-block border-2 border-white text-white hover:bg-white hover:text-primary px-8 py-4 rounded-lg font-medium transition-all duration-300"
          >
            View Our Work
          </router-link>
        </div>
      </div>
    </div>
  </div>

  <FooterComponent />
</template>

<script>
  import HeaderComponent from '@/components/Header.vue'
  import FooterComponent from '@/components/Footer.vue'
  import HeroComponent from '@/components/Hero.vue'
  import LoadingRipple from '@/components/LoadingRipple.vue'

  export default {
    name: 'ServicesView',
    components: {
      HeaderComponent,
      FooterComponent,
      HeroComponent,
      LoadingRipple
    },
    data() {
      return {
        services: [],
        isLoading: true,
        processSteps: [
          {
            title: "Discovery",
            description: "We start by understanding your goals, target audience, and vision to create a tailored strategy."
          },
          {
            title: "Design",
            description: "Our creative team develops stunning designs that align with your brand and user expectations."
          },
          {
            title: "Develop",
            description: "We bring designs to life with clean, efficient code and cutting-edge technologies."
          },
          {
            title: "Launch",
            description: "After thorough testing, we launch your project and provide ongoing support and optimization."
          }
        ]
      };
    },
    mounted() {
      this.loadServices();
      this.addServicesStructuredData();
    },
    methods: {
      async loadServices() {
        try {
          this.isLoading = true;
          
          // Try to load from NocoDB first
          try {
            const nocoService = (await import('@/services/nocodb.js')).default;
            const data = await nocoService.getServices();
            
            if (data && data.list && data.list.length > 0) {
              this.services = data.list.map(service => ({
                ...service,
                features: typeof service.features === 'string' 
                  ? service.features.split(', ').map(feature => feature.trim())
                  : service.features || [],
                delay: service.delay || 200
              }));
            }
          } catch (nocoError) {
            // Silently handle NocoDB connection issues
          }
          
          this.isLoading = false;
          this.initializeScrollAnimations();
        } catch (error) {
          this.isLoading = false;
          this.initializeScrollAnimations();
        }
      },
      initializeScrollAnimations() {
        // Initialize scroll animations
        this.$nextTick(() => {
          if (typeof sal !== 'undefined') {
            sal({
              duration: 500,
              threshold: 0.1,
              once: true
            });
          }
        });
      },
      addServicesStructuredData() {
        const servicesData = {
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "Diginova",
          "url": "https://diginova.ch",
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "Digital Services",
            "itemListElement": [
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Branding",
                  "description": "Professional branding services to create a strong brand identity that makes you stand out online.",
                  "serviceType": "Branding"
                }
              },
              {
                "@type": "Offer", 
                "itemOffered": {
                  "@type": "Service",
                  "name": "Web Design",
                  "description": "Custom web design services creating stunning visuals that speak for your brand and engage users.",
                  "serviceType": "Web Design"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service", 
                  "name": "Web Development",
                  "description": "Professional website development services creating modern, responsive websites that convert visitors to customers.",
                  "serviceType": "Web Development"
                }
              }
            ]
          }
        };
        
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.textContent = JSON.stringify(servicesData);
        document.head.appendChild(script);
      }
    }
  }
</script>